import CustomInput from "../../../CustomInput";
import { Button, styled } from "@mui/material";
import colors from "../../../../../theme/themeConstants";
import Captcha from "../../../Captcha";
import PrimaryButton from "components/core/PrimaryButton";
import React, { ChangeEvent, FormEvent, useCallback, useState } from "react";
import { login } from "services/Auth";

interface IProps {
  next: () => void;
}

const LoginWithEmail = ({ next }: IProps) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [captcha, setCaptcha] = useState("");
  const [resetCaptcha, setResetCaptcha] = useState<(() => void) | null>(null);

  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usernameError, setUsernameError] = useState<string | null>("");
  const [passwordError, setPasswordError] = useState<string | null>("");
  const [captchaError, setCaptchaError] = useState<string | null>("");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    validate();

    if (!isValid()) {
      return;
    }

    setError("");
    setIsSending(true);

    try {
      await login({ username, password, captcha });
    } catch (e: any) {
      const message = e?.response?.data?.error?.message || "Service unavailable";
      setError(message);
    } finally {
      setIsSending(false);
      handleCaptchaReset();
      if (resetCaptcha !== null) {
        resetCaptcha();
      }
    }
  };

  const validateUsername = useCallback((value: string) => {
    setUsernameError(value.length === 0 ? "Username required" : null);
  }, []);

  const validatePassword = useCallback((value: string) => {
    setPasswordError(value.length === 0 ? "Password required" : null);
  }, []);

  const validateCaptcha = useCallback((value: string) => {
    setCaptchaError(value.length === 0 ? "Captcha required" : null);
  }, []);

  const validate = useCallback(
    () => {
      validateUsername(username);
      validatePassword(password);
      validateCaptcha(captcha);
    },
    [
      validateUsername,
      validatePassword,
      validateCaptcha,
      username,
      password,
      captcha,
    ]
  );

  const isValid = useCallback(
    () => usernameError === null && passwordError === null && captchaError === null,
    [usernameError, passwordError, captchaError]
  );

  const handleChangeUsername = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
    validateUsername(e.target.value);
  }, [validateUsername]);

  const handleChangePassword = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    validatePassword(e.target.value);
  }, [validatePassword]);

  const handleCaptchaSuccess = useCallback((token: string) => {
    setCaptcha(token);
    validateCaptcha(token);
  }, [validateCaptcha]);

  const handleCaptchaReset = useCallback(() => {
    setCaptcha("");
    validateCaptcha("");
  }, [validateCaptcha]);

  const handleCaptchaRendered = useCallback((reset: () => void) => {
    setResetCaptcha(() => reset);
  }, []);

  const handleResetPassword = () => {
    next();
  };

  return (
    <Container>
      <form onSubmit={handleSubmit}>
        <Block>
          <label>Email or User Name</label>
          <CustomInput
            value={username}
            onChange={handleChangeUsername}
            error={!!usernameError}
            helperText={usernameError ?? ""}
            fullWidth
            autoComplete={"off"}
          />
        </Block>

        <Block>
          <label>Password</label>
          <CustomInput
            type="password"
            value={password}
            onChange={handleChangePassword}
            error={!!passwordError}
            helperText={passwordError ?? ""}
            fullWidth
            autoComplete={"off"}
          />
          <Button
            sx={{
              width: "fit-content",
              alignSelf: "flex-end",
              fontWeight: 500,
              fontSize: "12px",
              color: colors.textDark,
              fontFamily: "Poppins",
              opacity: 0.5,
            }}
            onClick={handleResetPassword}
          >
            Forgot password?
          </Button>
        </Block>

        <Block>
          <Captcha
            id="captcha"
            onSuccess={handleCaptchaSuccess}
            onFailed={handleCaptchaReset}
            onRendered={handleCaptchaRendered}
          />
        </Block>

        { error &&
          <Block>
            <ErrorMessage>
              { error }
            </ErrorMessage>
          </Block>
        }

        <PrimaryButton
          fullWidth
          type="submit"
          disabled={!isValid() || isSending}
        >
          Log in
        </PrimaryButton>
      </form>
    </Container>
  );
};

export default LoginWithEmail;

const Container = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "1rem"
});

const Block = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  marginBottom: "1rem",
  "& label": {
    fontSize: "12px",
    color: colors.textDark
  }
});

const ErrorMessage = styled("div")({
  color: "red",
  textAlign: "center",
  fontWeight: 400,
  fontSize: "0.75rem",
});
